import React, { useState } from 'react';
import { Box, Button, Card, CardContent, Typography, Stepper, Step, StepLabel, Radio, RadioGroup, FormControlLabel, FormControl, FormLabel, Checkbox, FormGroup } from '@mui/material';
import { useNavigate } from 'react-router-dom';

const steps = [
  'Work Location',
  'Energization Risk',
  'Primary Energy Source',
  'Issuer-Applied Safety Measures',
  'High-Risk Activities',
  'Result',
];

const highRiskOptions = [
  { label: 'Confined Space Entry', value: 'confined_space' },
  { label: 'Working at Heights', value: 'heights' },
  { label: 'Excavation', value: 'excavation' },
  { label: 'Hot Works', value: 'hot_works' },
];

const permitTypes = [
  { value: 'access', label: 'Access Permit' },
  { value: 'work_authority', label: 'Work Authority' },
  { value: 'test', label: 'Test Permit' },
  { value: 'general_work', label: 'General Work' },
];

const PermitWizard: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [answers, setAnswers] = useState({
    onOrNearEnergized: '',
    withinMAD: '',
    primaryEnergy: '',
    needsIASM: '',
    highRisk: [] as string[],
  });
  const navigate = useNavigate();

  const handleNext = () => setActiveStep((prev) => prev + 1);
  const handleBack = () => setActiveStep((prev) => prev - 1);

  // Step content logic
  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <FormControl component="fieldset">
            <FormLabel component="legend">Is the work on or near in-service or energizable equipment?</FormLabel>
            <RadioGroup
              value={answers.onOrNearEnergized}
              onChange={e => setAnswers(a => ({ ...a, onOrNearEnergized: e.target.value }))}
            >
              <FormControlLabel value="no" control={<Radio color="primary" />} label="No" />
              <FormControlLabel value="yes" control={<Radio color="primary" />} label="Yes" />
            </RadioGroup>
          </FormControl>
        );
      case 1:
        return (
          <FormControl component="fieldset">
            <FormLabel component="legend">Can the equipment be energized or is work within Minimum Approach Distance (MAD)?</FormLabel>
            <RadioGroup
              value={answers.withinMAD}
              onChange={e => setAnswers(a => ({ ...a, withinMAD: e.target.value }))}
            >
              <FormControlLabel value="no" control={<Radio color="primary" />} label="No" />
              <FormControlLabel value="yes" control={<Radio color="primary" />} label="Yes" />
            </RadioGroup>
          </FormControl>
        );
      case 2:
        return (
          <FormControl component="fieldset">
            <FormLabel component="legend">Will the task introduce a primary energy source (e.g. HV test, hydraulic pressure, etc.)?</FormLabel>
            <RadioGroup
              value={answers.primaryEnergy}
              onChange={e => setAnswers(a => ({ ...a, primaryEnergy: e.target.value }))}
            >
              <FormControlLabel value="yes" control={<Radio color="primary" />} label="Yes" />
              <FormControlLabel value="no" control={<Radio color="primary" />} label="No" />
            </RadioGroup>
          </FormControl>
        );
      case 3:
        return (
          <FormControl component="fieldset">
            <FormLabel component="legend">Does the work require Issuer-Applied Safety Measures (IASMs)?</FormLabel>
            <RadioGroup
              value={answers.needsIASM}
              onChange={e => setAnswers(a => ({ ...a, needsIASM: e.target.value }))}
            >
              <FormControlLabel value="yes" control={<Radio color="primary" />} label="Yes" />
              <FormControlLabel value="no" control={<Radio color="primary" />} label="No" />
            </RadioGroup>
          </FormControl>
        );
      case 4:
        return (
          <FormControl component="fieldset">
            <FormLabel component="legend">Does the work involve any high-risk activities?</FormLabel>
            <FormGroup>
              {highRiskOptions.map(opt => (
                <FormControlLabel
                  key={opt.value}
                  control={
                    <Checkbox
                      color="secondary"
                      checked={answers.highRisk.includes(opt.value)}
                      onChange={e => {
                        const checked = e.target.checked;
                        setAnswers(a => ({
                          ...a,
                          highRisk: checked
                            ? [...a.highRisk, opt.value]
                            : a.highRisk.filter(v => v !== opt.value),
                        }));
                      }}
                    />
                  }
                  label={opt.label}
                />
              ))}
            </FormGroup>
          </FormControl>
        );
      case 5:
        // Decision logic
        let permitType = '';
        if (answers.onOrNearEnergized === 'no') {
          permitType = 'general_work';
        } else if (answers.withinMAD === 'no') {
          permitType = 'work_authority';
        } else if (answers.primaryEnergy === 'yes') {
          permitType = 'test';
        } else if (answers.needsIASM === 'yes') {
          permitType = 'access';
        } else {
          permitType = 'work_authority';
        }
        return (
          <Box>
            <Typography variant="h5" color="primary" gutterBottom>
              Recommended Permit Type:
            </Typography>
            <Typography variant="h4" color="secondary" gutterBottom>
              {permitTypes.find(pt => pt.value === permitType)?.label || permitType}
            </Typography>
            {answers.highRisk.length > 0 && (
              <Box mt={2}>
                <Typography variant="h6" color="primary">
                  Additional High-Risk Permits Required:
                </Typography>
                <ul>
                  {answers.highRisk.map(hr => (
                    <li key={hr}>
                      <Typography color="secondary">{highRiskOptions.find(o => o.value === hr)?.label}</Typography>
                    </li>
                  ))}
                </ul>
              </Box>
            )}
            <Box mt={3}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => {
                  // Access and Test permits always use comprehensive form
                  if (permitType === 'access' || permitType === 'test') {
                    navigate('/permits/comprehensive', { 
                      state: { permitType, highRisk: answers.highRisk } 
                    });
                  } else {
                    // General Work and Work Authority use simple form
                    navigate('/permits/new', { 
                      state: { permitType, highRisk: answers.highRisk } 
                    });
                  }
                }}
              >
                Create {permitTypes.find(pt => pt.value === permitType)?.label || 'Permit'}
              </Button>
            </Box>
          </Box>
        );
      default:
        return null;
    }
  };

  // Stepper control
  const canProceed = () => {
    switch (activeStep) {
      case 0: return answers.onOrNearEnergized !== '';
      case 1: return answers.withinMAD !== '';
      case 2: return answers.primaryEnergy !== '';
      case 3: return answers.needsIASM !== '';
      default: return true;
    }
  };

  // Skip steps if not relevant
  React.useEffect(() => {
    if (activeStep === 1 && answers.onOrNearEnergized === 'no') setActiveStep(4);
    if (activeStep === 2 && answers.withinMAD === 'no') setActiveStep(4);
    if (activeStep === 3 && answers.primaryEnergy === 'yes') setActiveStep(4);
    if (activeStep === 4 && canProceed()) setActiveStep(5);
    // eslint-disable-next-line
  }, [activeStep, answers]);

  return (
    <Box maxWidth={600} mx="auto" mt={4}>
      <Card>
        <CardContent>
          <Typography variant="h3" color="primary" gutterBottom>
                            Re-GenOps Permit Selection Wizard
          </Typography>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
            {steps.map(label => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          <Box minHeight={180} mb={2}>{getStepContent(activeStep)}</Box>
          <Box display="flex" justifyContent="space-between">
            <Button
              disabled={activeStep === 0}
              onClick={handleBack}
              variant="outlined"
              color="primary"
            >
              Back
            </Button>
            {activeStep < steps.length - 1 && (
              <Button
                onClick={handleNext}
                variant="contained"
                color="secondary"
                disabled={!canProceed()}
              >
                Next
              </Button>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PermitWizard; 